/**
 * Supabase Equipment Service
 *
 * Supabase 版本的 Equipment Service，提供與 Firebase 版本完全相同的 API
 * 使用 Polling (pollingManager) 替代 Supabase Realtime
 */

import { supabase } from '@shared/services/core/supabaseClient'
import { pollingManager } from '@shared/services/core/pollingManager'
import { authService } from '@shared/services/auth/authService'
import type { User as SupabaseUser, PostgrestError } from '@supabase/supabase-js'

// ==================== 常數 ====================

const CACHE_DURATION = 5 * 60 * 1000 // 緩存有效期為 5 分鐘

// ==================== 類型定義 ====================

/** 告警設定 */
export interface AlarmSettings {
  timeoutEnabled?: boolean
  conditions?: Array<{
    field: string
    operator: string
    value: number | string
    severity?: string
  }>
  [key: string]: unknown
}

/** 告警狀態 */
export interface AlarmStatus {
  isAlarming?: boolean
  lastAlarmTime?: string | null
  alarmCount?: number
  [key: string]: unknown
}

/** 設備當前狀態 */
export interface CurrentStatus {
  [key: string]: unknown
}

/** 設備資料 */
export interface EquipmentData {
  [key: string]: unknown
}

/** 設備設定 */
export interface EquipmentConfig {
  [key: string]: unknown
}

/** 設備資料庫 row（Supabase snake_case 格式） */
export interface EquipmentRow {
  id: string
  field_id: string | null
  title?: string | null
  template?: string | null
  assets_code?: string | null
  is_error?: boolean
  is_show?: boolean
  use_ipc_id?: string | null
  alarm_settings?: AlarmSettings | null
  alarm_status?: AlarmStatus | null
  completion_date?: string | null
  created_at?: string
  created_by?: string | null
  updated_at?: string
  selected_device_type?: string | null
  directory_id?: string | null
  purpose?: string | null
  parent?: string | null
  sort?: number | null
  data_name?: string | null
  tdengine_super_table_name?: string | null
  tdengine_table_name?: string | null
  config?: EquipmentConfig | null
  current_status?: CurrentStatus | null
  data?: EquipmentData | null
  data_updated_at?: string | null
  maintain_kind?: string | null
  expand?: unknown
  image?: string | null
  remark?: string | null
}

/** 設備（Firebase camelCase 格式） */
export interface Equipment {
  id: string
  fieldId: string | null
  title?: string | null
  template?: string | null
  assetsCode?: string | null
  isError?: boolean
  isShow?: boolean
  useIPCId?: string | null
  alarmSettings?: AlarmSettings | null
  alarmStatus?: AlarmStatus | null
  completionDate?: string | null
  createdAt?: string
  createdBy?: string | null
  updatedAt?: string
  selectedDeviceType?: string | null
  directoryId?: string | null
  purpose?: string | null
  parent?: string | null
  sort?: number | null
  dataName?: string | null
  tdengineSuperTableName?: string | null
  tdengineTableName?: string | null
  config?: EquipmentConfig | null
  currentStatus?: CurrentStatus | null
  data?: EquipmentData | null
  dataUpdatedAt?: string | null
  maintainKind?: string | null
  expand?: unknown
  image?: string | null
  remark?: string | null
  [key: string]: unknown
}

/** 取得設備列表參數 */
export interface GetEquipmentsParams {
  fieldId?: string | string[] | null
  template?: string | null
  isError?: boolean | null
}

/** 取得設備 key 列表參數 */
export interface GetEquipmentKeysParams {
  fieldIds?: string[]
  template?: string | null
  templateFirst?: boolean
  checkIPC?: boolean
}

/** 取得監控設備參數 */
export interface GetMonitoredEquipmentsParams {
  fieldIds?: string[] | null
  excludeTemplates?: string[]
  excludeFieldIds?: string[]
}

/** 設備統計結果 */
export interface EquipmentStats {
  byField: {
    [fieldId: string]: {
      count: number
      templates: { [template: string]: number }
    }
  }
  byTemplate: { [template: string]: number }
  total: number
}

/** 訂閱回調函數 */
export type EquipmentCallback = (equipment: Equipment | null) => void
export type EquipmentsCallback = (equipments: Equipment[]) => void

/** 監聯器資訊 */
interface ListenerInfo {
  equipmentId?: string
  pollKey?: string
  unsubPoll?: () => void
}

/** 緩存項目 */
interface CacheEntry {
  data: Equipment[]
  timestamp: number
}

// ==================== 欄位映射 ====================

/**
 * 欄位名稱映射：Firebase (camelCase) ↔ Supabase (snake_case)
 */
const FIELD_MAP: Record<string, string> = {
  // Firebase → Supabase
  fieldId: 'field_id',
  assetsCode: 'assets_code',
  isError: 'is_error',
  isShow: 'is_show',
  useIPCId: 'use_ipc_id',
  alarmSettings: 'alarm_settings',
  alarmStatus: 'alarm_status',
  completionDate: 'completion_date',
  createdAt: 'created_at',
  createdBy: 'created_by',
  updatedAt: 'updated_at',
  // 新增欄位
  selectedDeviceType: 'selected_device_type',
  directoryId: 'directory_id',
  purpose: 'purpose',
  parent: 'parent',
  sort: 'sort',
  dataName: 'data_name',
  tdengineSuperTableName: 'tdengine_super_table_name',
  tdengineTableName: 'tdengine_table_name',
  config: 'config',
  currentStatus: 'current_status',
  data: 'data',
  dataUpdatedAt: 'data_updated_at',
  maintainKind: 'maintain_kind',
  expand: 'expand',
  image: 'image',
  remark: 'remark',
}

// 反向映射：Supabase → Firebase
const REVERSE_FIELD_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(FIELD_MAP).map(([k, v]) => [v, k])
)

// ==================== Service 類別 ====================

class SupabaseEquipmentService {
  private listeners: Record<string, ListenerInfo> = {}
  private activeSubscriptions: Set<string> = new Set()
  private cache: Record<string, CacheEntry> = {}

  constructor() {
    // 注意：認證狀態由 authService 統一管理
    // 使用 authService.currentUser 取得當前用戶
  }

  // ==================== 屬性 ====================

  /**
   * 取得當前用戶（委派給 authService）
   */
  get currentUser(): SupabaseUser | null {
    const user = authService.currentUser
    if (!user) return null
    return (user as { supabaseUser?: SupabaseUser }).supabaseUser || user as unknown as SupabaseUser
  }

  get activeListenersCount(): number {
    return this.activeSubscriptions.size
  }

  // ==================== 私有方法：資料轉換 ====================

  /**
   * 將 Firebase 格式轉換為 Supabase 格式（用於寫入）
   */
  private _transformToSupabaseFormat(data: Partial<Equipment>): Partial<EquipmentRow> {
    const result: Record<string, unknown> = {}

    for (const [key, value] of Object.entries(data)) {
      const supabaseKey = FIELD_MAP[key] || key
      result[supabaseKey] = value
    }

    // 處理日期格式
    if (result.completion_date && typeof result.completion_date === 'string') {
      try {
        const date = new Date(result.completion_date as string)
        if (!isNaN(date.getTime())) {
          result.completion_date = date.toISOString().split('T')[0]
        }
      } catch (e) {
        result.completion_date = null
      }
    }

    return result as Partial<EquipmentRow>
  }

  /**
   * 將 Supabase 格式轉換為 Firebase 格式（用於讀取）
   */
  private _transformFromSupabaseFormat(data: EquipmentRow | null): Equipment | null {
    if (!data) return null

    const result: Record<string, unknown> = {}

    for (const [key, value] of Object.entries(data)) {
      const firebaseKey = REVERSE_FIELD_MAP[key] || key
      result[firebaseKey] = value
    }

    return result as Equipment
  }

  /**
   * 批量轉換 Supabase 資料為 Firebase 格式
   */
  private _transformArrayFromSupabase(rows: EquipmentRow[] | null): Equipment[] {
    if (!rows || !Array.isArray(rows)) return []
    return rows.map(row => this._transformFromSupabaseFormat(row)).filter((eq): eq is Equipment => eq !== null)
  }

  // ==================== 認證相關 ====================

  /**
   * 安全獲取當前用戶（委派給 authService）
   */
  getCurrentUser(): SupabaseUser | null {
    return this.currentUser
  }

  // ==================== 單一設備訂閱 ====================

  /**
   * 訂閱單一設備的即時更新（輪詢模式，5s）
   */
  subscribeToSingleEquipment(equipmentId: string, callback: EquipmentCallback): () => void {
    if (!equipmentId) {
      return () => {}
    }

    const pollKey = `equipment:${equipmentId}`
    const listenerKey = `single_${equipmentId}`

    const unsubPoll = pollingManager.register({
      key: pollKey,
      fetchFn: async () => {
        const equipment = await this.getEquipment(equipmentId)
        callback(equipment)
      },
      intervalMs: 5_000,
      pauseWhenHidden: true,
      immediate: true,
    })

    this.activeSubscriptions.add(equipmentId)
    this.listeners[listenerKey] = { equipmentId, pollKey, unsubPoll }

    return () => {
      unsubPoll()
      delete this.listeners[listenerKey]
      this.activeSubscriptions.delete(equipmentId)
    }
  }

  /**
   * 移除單一設備的所有監聽器
   */
  removeAllListenersForEquipment(equipmentId: string): void {
    const listenerKey = `single_${equipmentId}`
    const listener = this.listeners[listenerKey]
    if (listener?.unsubPoll) {
      listener.unsubPoll()
    } else {
      pollingManager.unregister(`equipment:${equipmentId}`)
    }
    delete this.listeners[listenerKey]
    this.activeSubscriptions.delete(equipmentId)
  }

  /**
   * 移除單一設備監聽器
   */
  removeListener(equipmentId: string): void {
    this.removeAllListenersForEquipment(equipmentId)
    console.log(`成功移除設備 ${equipmentId} 的監聽器`)
  }

  // ==================== 設備列表訂閱 ====================

  /**
   * 訂閱設備列表的即時更新（輪詢模式，15s）
   */
  subscribeToEquipments(
    { fieldId = null, template = null, isError = null }: GetEquipmentsParams,
    callback: EquipmentsCallback
  ): string {
    const listenerKey = `${template || 'all'}_${fieldId || 'all'}_${isError !== null ? isError : 'all'}`
    const pollKey = `equipments:${listenerKey}`

    const unsubPoll = pollingManager.register({
      key: pollKey,
      fetchFn: async () => {
        const equipments = await this.getEquipments({ fieldId, template, isError })
        callback(equipments)
      },
      intervalMs: 15_000,
      pauseWhenHidden: true,
      immediate: true,
    })

    this.listeners[listenerKey] = { pollKey, unsubPoll }

    return listenerKey
  }

  /**
   * 取消設備列表訂閱
   */
  unsubscribeFromEquipments(listenerKey: string): void {
    if (this.listeners[listenerKey]) {
      const { unsubPoll } = this.listeners[listenerKey]
      if (unsubPoll) {
        unsubPoll()
      }
      delete this.listeners[listenerKey]
    }
  }

  // ==================== 告警訂閱 ====================

  /**
   * 訂閱有告警狀態的設備（輪詢模式，15s）
   */
  subscribeToEquipmentsWithAlarmStatus(callback: EquipmentsCallback): () => void {
    const pollKey = 'equipments:alarm_status'
    const listenerKey = `alarm_global`

    const unsubPoll = pollingManager.register({
      key: pollKey,
      fetchFn: async () => {
        const equipments = await this.getEquipmentsWithAlarmStatus()
        callback(equipments)
      },
      intervalMs: 15_000,
      pauseWhenHidden: true,
      immediate: true,
    })

    this.listeners[listenerKey] = { pollKey, unsubPoll }

    return () => {
      unsubPoll()
      delete this.listeners[listenerKey]
    }
  }

  /**
   * 訂閱特定案場的告警設備（輪詢模式，15s）
   */
  subscribeToEquipmentsWithAlarmStatusByFieldId(fieldId: string, callback: EquipmentsCallback): () => void {
    if (!fieldId) {
      console.warn('必須提供 fieldId 才能訂閱特定案場的告警')
      callback([])
      return () => {}
    }

    const pollKey = `equipments:alarm_status:${fieldId}`
    const listenerKey = `alarm_field_${fieldId}`

    const unsubPoll = pollingManager.register({
      key: pollKey,
      fetchFn: async () => {
        const { data, error } = await supabase
          .from('equipment')
          .select('*')
          .eq('field_id', fieldId)
          .eq('alarm_status->>isAlarming', 'true')

        if (error) {
          console.error(`案場 ${fieldId} 告警設備查詢失敗:`, error)
          // 查詢失敗時保留上次狀態，不清空
          return
        }

        callback(this._transformArrayFromSupabase(data as EquipmentRow[]))
      },
      intervalMs: 15_000,
      pauseWhenHidden: true,
      immediate: true,
    })

    this.listeners[listenerKey] = { pollKey, unsubPoll }

    return () => {
      unsubPoll()
      delete this.listeners[listenerKey]
    }
  }

  // ==================== 移除所有監聽器 ====================

  removeAllListeners(): void {
    console.log('[supabaseEquipmentService] 開始移除所有監聽器...')

    for (const [, listener] of Object.entries(this.listeners)) {
      if (listener.unsubPoll) {
        listener.unsubPoll()
      } else if (listener.pollKey) {
        pollingManager.unregister(listener.pollKey)
      }
    }

    this.listeners = {}
    this.activeSubscriptions.clear()

    console.log('[supabaseEquipmentService] 已移除所有監聽器')
  }

  // ==================== 緩存管理 ====================

  clearCache(): void {
    this.cache = {}
  }

  isCacheValid(cacheEntry: CacheEntry): boolean {
    return cacheEntry && (Date.now() - cacheEntry.timestamp < CACHE_DURATION)
  }

  // ==================== CRUD 方法 ====================

  /**
   * 獲取單一設備
   */
  async getEquipment(equipmentId: string): Promise<Equipment | null> {
    const { data, error } = await supabase
      .from('equipment')
      .select('*')
      .eq('id', equipmentId)
      .single()

    if (error) {
      if ((error as PostgrestError).code === 'PGRST116') {
        // 記錄不存在
        return null
      }
      console.error(`[supabaseEquipmentService] Error getting equipment ${equipmentId}:`, error)
      return null
    }

    return this._transformFromSupabaseFormat(data as EquipmentRow)
  }

  /**
   * 批量獲取設備（根據 ID 列表）
   */
  async getEquipmentsByIds(equipmentIds: string[]): Promise<Equipment[]> {
    if (!equipmentIds || equipmentIds.length === 0) {
      return []
    }

    const { data, error } = await supabase
      .from('equipment')
      .select('*')
      .in('id', equipmentIds)

    if (error) {
      console.error('[supabaseEquipmentService] Error getting equipments by ids:', error)
      return []
    }

    return (data || []).map(row => this._transformFromSupabaseFormat(row as EquipmentRow))
  }

  /**
   * 獲取設備列表
   */
  async getEquipments({ fieldId = null, template = null, isError = null }: GetEquipmentsParams): Promise<Equipment[]> {
    let query = supabase.from('equipment').select('*')

    // 處理 fieldId 過濾
    if (fieldId) {
      if (Array.isArray(fieldId)) {
        query = query.in('field_id', fieldId)
      } else {
        query = query.eq('field_id', fieldId)
      }
    }

    // 處理 template 過濾
    if (template) {
      query = query.eq('template', template)
    }

    // 處理 isError 過濾
    if (isError !== null) {
      query = query.eq('is_error', isError)
    }

    const { data, error } = await query

    if (error) {
      console.error('[supabaseEquipmentService] Error loading equipments:', error)
      throw error
    }

    return this._transformArrayFromSupabase(data as EquipmentRow[])
  }

  /**
   * 獲取有告警設定的設備
   */
  async getEquipmentsWithAlarmSettings(): Promise<Equipment[]> {
    const { data, error } = await supabase
      .from('equipment')
      .select('*')
      .not('alarm_settings', 'is', null)
      .neq('alarm_settings', '{}')

    if (error) {
      console.error('[supabaseEquipmentService] Error loading equipments with alarm settings:', error)
      throw error
    }

    return this._transformArrayFromSupabase(data as EquipmentRow[])
  }

  /**
   * 獲取告警中的設備
   */
  async getEquipmentsInAlarm(): Promise<Equipment[]> {
    const { data, error } = await supabase
      .from('equipment')
      .select('*')
      .eq('alarm_status->>isAlarming', 'true')

    if (error) {
      console.error('[supabaseEquipmentService] Error loading equipments in alarm:', error)
      throw error
    }

    return this._transformArrayFromSupabase(data as EquipmentRow[])
  }

  /**
   * 獲取有告警狀態的設備（優化版）
   * 只返回：
   * 1. 有設定告警規則的設備 (alarm_settings.timeoutEnabled 或有 conditions)
   * 2. 或正在告警中的設備 (alarm_status.isAlarming = true)
   */
  async getEquipmentsWithAlarmStatus(): Promise<Equipment[]> {
    // 使用 OR 條件查詢：正在告警 OR 有設定告警規則
    const { data, error } = await supabase
      .from('equipment')
      .select('*')
      .or('alarm_status->>isAlarming.eq.true,alarm_settings->>timeoutEnabled.eq.true,alarm_settings->conditions.neq.[]')

    if (error) {
      console.error('[supabaseEquipmentService] Error loading equipments with alarm status:', error)
      throw error
    }

    // 額外過濾：確保 alarm_settings 有實際內容
    const filtered = (data as EquipmentRow[]).filter(eq => {
      // 正在告警的設備一定要顯示
      if (eq.alarm_status?.isAlarming === true) return true

      // 有設定告警規則的設備
      if (eq.alarm_settings) {
        if (eq.alarm_settings.timeoutEnabled === true) return true
        if (Array.isArray(eq.alarm_settings.conditions) && eq.alarm_settings.conditions.length > 0) return true
      }

      return false
    })

    return this._transformArrayFromSupabase(filtered)
  }

  /**
   * 搜索設備（帶緩存）
   */
  async searchEquipment(fieldId: string): Promise<Equipment[]> {
    // 檢查緩存
    if (this.cache[fieldId] && this.isCacheValid(this.cache[fieldId])) {
      return this.cache[fieldId].data
    }

    const { data, error } = await supabase
      .from('equipment')
      .select('*')
      .eq('field_id', fieldId)

    if (error) {
      console.error(`[supabaseEquipmentService] Error loading equipment for field ${fieldId}:`, error)
      throw error
    }

    const equipments = this._transformArrayFromSupabase(data as EquipmentRow[])

    // 更新緩存
    this.cache[fieldId] = {
      data: equipments,
      timestamp: Date.now()
    }

    return equipments
  }

  /**
   * 獲取設備 ID 列表
   */
  async getEquipmentKeys({ fieldIds = [], template = null, templateFirst = true, checkIPC = false }: GetEquipmentKeysParams): Promise<string[]> {
    // 參數驗證
    let normalizedFieldIds = fieldIds
    if (!Array.isArray(normalizedFieldIds)) {
      console.warn('fieldIds 必須是數組')
      normalizedFieldIds = []
    }

    let shouldCheckIPC = checkIPC
    if (template === 'ipc') shouldCheckIPC = false

    let query = supabase.from('equipment').select('id, field_id, template, use_ipc_id')

    // 根據條件建立查詢
    if (templateFirst && template) {
      query = query.eq('template', template)
      if (normalizedFieldIds.length > 0) {
        query = query.in('field_id', normalizedFieldIds)
      }
    } else if (normalizedFieldIds.length > 0) {
      query = query.in('field_id', normalizedFieldIds)
      if (template) {
        query = query.eq('template', template)
      }
    } else if (template) {
      query = query.eq('template', template)
    }

    // 如果需要檢查 IPC
    if (shouldCheckIPC) {
      query = query.not('use_ipc_id', 'is', null)
    }

    const { data, error } = await query

    if (error) {
      console.error('[supabaseEquipmentService] 獲取設備 key 列表時發生錯誤:', error)
      throw error
    }

    return (data as Array<{ id: string }>).map(row => row.id)
  }

  // ==================== 寫入方法 ====================

  /**
   * 建立新設備
   */
  async createEquipment(equipmentData: Partial<Equipment>, fieldId: string): Promise<string> {
    const user = this.getCurrentUser()
    if (!user) {
      throw new Error('用戶未認證')
    }

    let equipmentId = equipmentData.id

    // 如果沒有提供 ID，生成一個新的唯一 ID
    if (!equipmentId) {
      equipmentId = crypto.randomUUID()
    } else {
      // 檢查是否已存在
      const existing = await this.getEquipment(equipmentId)
      if (existing) {
        throw new Error('具有相同 ID 的設備已存在')
      }
    }

    // 準備新設備資料
    const newEquipmentData: Partial<EquipmentRow> = {
      ...this._transformToSupabaseFormat(equipmentData),
      id: equipmentId,
      field_id: fieldId,
      created_at: new Date().toISOString(),
      created_by: user.id,
      updated_at: new Date().toISOString()
    }

    const { error } = await supabase
      .from('equipment')
      .insert(newEquipmentData)

    if (error) {
      console.error('[supabaseEquipmentService] Error creating equipment:', error)
      throw error
    }

    return equipmentId
  }

  /**
   * 更新設備（部分更新）
   */
  async updateEquipment(equipmentId: string, equipmentData: Partial<Equipment>): Promise<void> {
    const updateData: Partial<EquipmentRow> = {
      ...this._transformToSupabaseFormat(equipmentData),
      updated_at: new Date().toISOString()
    }

    const { error } = await supabase
      .from('equipment')
      .update(updateData)
      .eq('id', equipmentId)

    if (error) {
      console.error('[supabaseEquipmentService] Error updating equipment:', error)
      throw error
    }

    this.clearCache()
  }

  /**
   * 強制更新設備（完整覆蓋）
   */
  async forceUpdateEquipment(equipmentId: string, equipmentData: Partial<Equipment>): Promise<void> {
    const updateData: Partial<EquipmentRow> = {
      ...this._transformToSupabaseFormat(equipmentData),
      id: equipmentId,
      updated_at: new Date().toISOString()
    }

    const { error } = await supabase
      .from('equipment')
      .upsert(updateData, { onConflict: 'id' })

    if (error) {
      console.error('[supabaseEquipmentService] Error force updating equipment:', error)
      throw error
    }

    this.clearCache()
  }

  /**
   * 獲取有監控的設備（用於設備總覽頁面優化）
   * 只返回 IPC 設備或有 useIPCId 的設備
   */
  async getMonitoredEquipments({ fieldIds = null, excludeTemplates = [], excludeFieldIds = [] }: GetMonitoredEquipmentsParams = {}): Promise<Equipment[]> {
    let query = supabase
      .from('equipment')
      .select('id, field_id, title, template, selected_device_type, use_ipc_id, data, data_updated_at, alarm_status, updated_at')
      .or('template.eq.ipc,and(use_ipc_id.not.is.null,use_ipc_id.neq.none)')

    // 排除特定設備類型
    if (excludeTemplates.length > 0) {
      // 使用 not.in 過濾
      query = query.not('template', 'in', `(${excludeTemplates.join(',')})`)
    }

    // 過濾案場
    if (fieldIds && fieldIds.length > 0) {
      query = query.in('field_id', fieldIds)
    }

    // 排除特定案場
    if (excludeFieldIds.length > 0) {
      query = query.not('field_id', 'in', `(${excludeFieldIds.join(',')})`)
    }

    const { data, error } = await query

    if (error) {
      console.error('[supabaseEquipmentService] Error loading monitored equipments:', error)
      throw error
    }

    return this._transformArrayFromSupabase(data as EquipmentRow[])
  }

  /**
   * 獲取設備統計（按案場和類型分組）
   */
  async getEquipmentStats({ excludeTemplates = [], excludeFieldIds = [] }: GetMonitoredEquipmentsParams = {}): Promise<EquipmentStats> {
    let query = supabase
      .from('equipment')
      .select('field_id, template, use_ipc_id')
      .or('template.eq.ipc,and(use_ipc_id.not.is.null,use_ipc_id.neq.none)')

    if (excludeTemplates.length > 0) {
      query = query.not('template', 'in', `(${excludeTemplates.join(',')})`)
    }

    if (excludeFieldIds.length > 0) {
      query = query.not('field_id', 'in', `(${excludeFieldIds.join(',')})`)
    }

    const { data, error } = await query

    if (error) {
      console.error('[supabaseEquipmentService] Error loading equipment stats:', error)
      throw error
    }

    // 統計各案場的設備數量和類型
    const stats: EquipmentStats = {
      byField: {},
      byTemplate: {},
      total: data?.length || 0
    }

    if (data) {
      (data as Array<{ field_id: string | null; template: string | null }>).forEach(eq => {
        // 按案場統計
        if (eq.field_id) {
          if (!stats.byField[eq.field_id]) {
            stats.byField[eq.field_id] = { count: 0, templates: {} }
          }
          stats.byField[eq.field_id].count++
          const template = eq.template || 'unknown'
          stats.byField[eq.field_id].templates[template] =
            (stats.byField[eq.field_id].templates[template] || 0) + 1
        }

        // 按類型統計
        const template = eq.template || 'unknown'
        stats.byTemplate[template] = (stats.byTemplate[template] || 0) + 1
      })
    }

    return stats
  }

  /**
   * 刪除設備
   */
  async deleteEquipment(equipmentId: string): Promise<void> {
    const user = this.getCurrentUser()
    if (!user) {
      throw new Error('用戶未認證')
    }

    if (!equipmentId) {
      console.error('沒有設備ID')
      return
    }

    const { error } = await supabase
      .from('equipment')
      .delete()
      .eq('id', equipmentId)

    if (error) {
      console.error('[supabaseEquipmentService] Error deleting equipment:', error)
      throw error
    }

    this.clearCache()
  }
}

// 單例模式
const supabaseEquipmentService = new SupabaseEquipmentService()
export default supabaseEquipmentService
